import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { Card, CardContent, Button, Input, Alert } from '@license-management/ui';
import { Lock, AlertCircle } from 'lucide-react';

function SetPasswordPage() {
  const { setPassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      await setPassword(currentPassword, newPassword);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-600 text-white mb-4 shadow-lg shadow-primary-600/25">
            <Lock className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Set New Password
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            You must change your password before continuing
          </p>
        </div>

        {/* Password Form Card */}
        <Card shadow="elevated">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <Alert
                  variant="error"
                  icon={<AlertCircle className="h-5 w-5" />}
                >
                  {error}
                </Alert>
              )}

              <Input
                label="Current Password (Temporary Password)"
                type="password"
                id="currentPassword"
                name="currentPassword"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your temporary password"
              />

              <Input
                label="New Password"
                type="password"
                id="newPassword"
                name="newPassword"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                helperText="Password must be at least 8 characters"
              />

              <Input
                label="Confirm New Password"
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />

              <Button
                type="submit"
                loading={isLoading}
                className="w-full"
                size="lg"
              >
                {isLoading ? 'Setting password...' : 'Set Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SetPasswordPage;
